import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { LogoMark } from "@/components/LogoMark";
import { StoreFooter } from "@/components/StoreFooter";

function StorefrontHeader() {
  return (
    <>
      <div className="gov-strip">
        <span className="tri"><span className="b saffron" /><span className="b white" /><span className="b green" /></span>
        <Link to="/about">About us</Link>
        <Link to="/authorised-users">Authorised users</Link>
        <Link to="/contact">Contact</Link>
      </div>
      <header className="store-header">
        <Link to="/" className="store-brand"><LogoMark small /><span>GI Bharat<small>By True Vision Welfare Society</small></span></Link>
        <nav><Link to="/login" className="store-login">Sign in</Link></nav>
      </header>
    </>
  );
}

export default function ContactUs() {
  const [info, setInfo] = useState({ address: "", email: "", phone: "" });
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { api.get("/contact-info").then(r => setInfo(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      toast.success("Message sent");
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  return (
    <main className="storefront" data-testid="contact-page">
      <StorefrontHeader />

      <section className="section">
        <p className="eyebrow">GET IN TOUCH</p>
        <h1 style={{ color: "var(--tv-green)" }}>Contact True Vision Welfare Society</h1>
        <p className="subtitle">We're happy to hear from artisans, buyers, government bodies, and partners. Drop us a message and our team will reach out.</p>
      </section>

      <section className="section" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30, alignItems: "start" }}>
        <div>
          <div className="admin-tile" style={{ marginBottom: 14 }}>
            <p className="eyebrow" style={{ margin: 0 }}>OFFICE</p>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 8 }}>
              <MapPin size={18} style={{ color: "var(--tv-green)" }} />
              <div data-testid="contact-address"><b>True Vision Welfare Society</b><br /><span className="muted">{info.address || "GI authorised user services · Est. 2017 · Working for GI since 2023"}</span></div>
            </div>
          </div>
          <div className="admin-tile" style={{ marginBottom: 14 }}>
            <p className="eyebrow" style={{ margin: 0 }}>EMAIL</p>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
              <Mail size={18} style={{ color: "var(--tv-green)" }} />
              <a href={`mailto:${info.email || "contact@truevision.example"}`} style={{ color: "var(--tv-green)" }} data-testid="contact-info-email">{info.email || "contact@truevision.example"}</a>
            </div>
          </div>
          <div className="admin-tile">
            <p className="eyebrow" style={{ margin: 0 }}>PHONE</p>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
              <Phone size={18} style={{ color: "var(--tv-green)" }} />
              <span data-testid="contact-info-phone">{info.phone || "+91-XXXXX-XXXXX"}</span>
            </div>
          </div>
        </div>

        <form className="form-section" onSubmit={submit} data-testid="contact-form">
          <div className="field-grid">
            <label>Your name<input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></label>
            <label>Phone<input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" placeholder="+91…" /></label>
          </div>
          <label>Email<input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /></label>
          <label>Subject<input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" placeholder="How can we help?" /></label>
          <label>Message<textarea required rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} data-testid="contact-message" /></label>
          {sent && <div className="success" data-testid="contact-success">Thanks — your message is with our team.</div>}
          <div className="form-actions">
            <span className="muted">We reply within 2 working days.</span>
            <button className="primary-btn" disabled={busy} data-testid="contact-submit">{busy ? "Sending…" : <>Send message <Send size={14} /></>}</button>
          </div>
        </form>
      </section>

      <StoreFooter />
    </main>
  );
}
